export type TranscriptDeliveryResult = 'download' | 'fallback';

export interface DeliverTranscriptOptions {
  content: string;
  filename: string;
  /** When true, skip blob download and open copy view (mobile fallback). */
  forceFallback?: boolean;
}

/**
 * Attempts a direct file download. Returns `fallback` when the browser is
 * unlikely to save the file (common on iOS Safari / in-app browsers).
 */
export function deliverTranscript({
  content,
  filename,
  forceFallback = false,
}: DeliverTranscriptOptions): TranscriptDeliveryResult {
  if (forceFallback || shouldUseTranscriptFallback()) {
    return deliverViaFallback(content);
  }

  try {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    // Revoke after the browser has picked up the blob URL for download.
    window.setTimeout(() => {
      URL.revokeObjectURL(url);
      if (anchor.parentNode) {
        anchor.parentNode.removeChild(anchor);
      }
    }, 0);
    return 'download';
  } catch {
    return deliverViaFallback(content);
  }
}

export function shouldUseTranscriptFallback(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  const isInAppBrowser = /FBAN|FBAV|Instagram|Line\/|Twitter|LinkedInApp/i.test(ua);

  return (isIOS && isSafari) || isInAppBrowser;
}

function deliverViaFallback(content: string): TranscriptDeliveryResult {
  if (openTranscriptCopyView(content)) return 'fallback';
  if (tryCopyTranscriptToClipboard(content)) return 'fallback';
  throw new Error('transcript_delivery_failed');
}

/** Opens a copy-friendly view in a new tab. Returns false when popups are blocked. */
export function openTranscriptCopyView(content: string): boolean {
  const popup = window.open('', '_blank', 'noopener,noreferrer');
  if (!popup) return false;

  popup.document.title = 'Conversation transcript';
  popup.document.body.style.fontFamily = 'system-ui, sans-serif';
  popup.document.body.style.padding = '16px';
  popup.document.body.style.whiteSpace = 'pre-wrap';
  popup.document.body.style.lineHeight = '1.5';

  const hint = popup.document.createElement('p');
  hint.textContent = 'Select all and copy to save this transcript.';
  hint.style.color = '#555';
  hint.style.fontSize = '14px';

  const pre = popup.document.createElement('pre');
  pre.textContent = content;
  pre.style.marginTop = '12px';
  pre.style.fontFamily = 'inherit';
  pre.style.whiteSpace = 'pre-wrap';

  popup.document.body.appendChild(hint);
  popup.document.body.appendChild(pre);
  return true;
}

/** Last-resort fallback when popups are blocked (e.g. embedded widget contexts). */
function tryCopyTranscriptToClipboard(content: string): boolean {
  try {
    const textarea = document.createElement('textarea');
    textarea.value = content;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}
